import { useEffect, useMemo, useState } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Search, ImageOff, Pencil } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { ComboBox, type OpcaoCombo } from '../../components/ComboBox';
import { useGridNavegavel } from '../../lib/useGridNavegavel';
import { LightboxImagem } from '../atendimento/componentes/LightboxImagem';
import { FotoProduto } from '../../components/FotoProduto';
import { formatMoeda, abaixoDaMeta } from '@fiodecobre/shared';

// O corte de "margem ruim" era 30% cravado aqui; desde a migration 0300 e' a META do produto
// (categoria, senao a padrao da loja — Cadastros > Metas de Margem), que vem na rota. 30 so e'
// usado se a rota nao trouxer a meta.
const MRG_RUIM_FALLBACK = 30;
const MRG_OK = 60;
function metaDe(p: { markup_meta: string | number | null }): number {
  const m = Number(p.markup_meta);
  return p.markup_meta != null && Number.isFinite(m) ? m : MRG_RUIM_FALLBACK;
}

function fmtMoneyInput(val: any): string {
  if (val === '' || val == null) return '';
  let n: number;
  if (typeof val === 'number') n = val;
  else {
    const s = String(val);
    n = /,/.test(s) ? parseFloat(s.replace(/\./g, '').replace(',', '.')) : parseFloat(s);
  }
  if (!Number.isFinite(n)) return String(val);
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function parseMoney(val: any): number {
  if (val === '' || val == null) return 0;
  if (typeof val === 'number') return val;
  const s = String(val);
  return (/,/.test(s) ? parseFloat(s.replace(/\./g, '').replace(',', '.')) : parseFloat(s)) || 0;
}

function calcMargem(custo: any, pix: any): number | null {
  const c = Number(custo);
  const p = Number(pix);
  if (!Number.isFinite(c) || !Number.isFinite(p) || c <= 0) return null;
  return ((p - c) / c) * 100;
}

function corMargem(mrg: number | null, ruim: boolean): { bg: string; text: string; label: string } {
  if (mrg == null) return { bg: 'transparent', text: 'opacity-30', label: '—' };
  if (ruim) return { bg: '#fde68a', text: 'text-red-700', label: `${mrg.toFixed(1)}%` };
  if (mrg < MRG_OK) return { bg: '#fef3c7', text: 'text-amber-700', label: `${mrg.toFixed(1)}%` };
  return { bg: '#d1fae5', text: 'text-green-700', label: `${mrg.toFixed(1)}%` };
}

interface Produto {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  preco_custo: string;
  preco_pix: string;
  preco_cartao: string;
  estoque_atual: string;
  localizacao: string | null; // LEGADO (texto congelado ate a 0148) — a tela nao escreve mais nele
  localizacao_id: number | null;
  localizacao_codigo: string | null;
  unidade_compra: string | null;
  qtd_embalagem_padrao: string | null;
  unidade_sigla: string | null;
  marca_nome: string | null;
  nf_unidade: string | null;
  nf_quantidade: string | null;
  nf_valor_unitario: string | null;
  markup_meta: string | null; // meta de markup do produto (migration 0300)
}

export function NotaEntradaRevisarMargens() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [soRuins, setSoRuins] = useState(false);
  const [opcoesLocal, setOpcoesLocal] = useState<OpcaoCombo[]>([]);

  useEffect(() => {
    if (!id) return;
    setCarregando(true);
    api.get<Produto[]>(`/api/notas-entrada/${id}/produtos-revisao`)
      .then(setProdutos)
      .catch(() => setProdutos([]))
      .finally(() => setCarregando(false));
  }, [id]);

  // Cod Fornecedor de cada produto NESTA nota (pedido dele, 04/11/2026). Vem do `GET /:id` da nota,
  // que ja traz o item inteiro — evita mexer na rota da revisao (e reiniciar o servidor) por um campo.
  // Falhou, a coluna mostra "—": e' so leitura.
  const [codFornPorProduto, setCodFornPorProduto] = useState<Map<number, string>>(() => new Map());
  useEffect(() => {
    if (!id) return;
    api.get<{ itens: { produto_id: number | null; codigo_fornecedor: string | null }[] }>(`/api/notas-entrada/${id}`)
      .then((n) => {
        const m = new Map<number, string>();
        for (const it of n.itens || []) {
          if (it.produto_id && it.codigo_fornecedor && !m.has(it.produto_id)) m.set(it.produto_id, it.codigo_fornecedor);
        }
        setCodFornPorProduto(m);
      })
      .catch(() => setCodFornPorProduto(new Map()));
  }, [id]);

  useEffect(() => {
    void (async () => {
      try {
        const lista = await api.get<{ id: number; codigo: string }[]>('/api/localizacoes');
        setOpcoesLocal(lista.map((l) => ({ value: l.id, label: l.codigo })));
      } catch { /* sem lista o combo so nao oferece nada; o resto da tela segue */ }
    })();
  }, []);

  const filtrados = useMemo(() => {
    return produtos.filter(p => {
      if (soRuins && !abaixoDaMeta(p.preco_pix, p.preco_custo, metaDe(p))) return false;
      if (busca.trim()) {
        const q = busca.trim().toUpperCase();
        const txt = `${p.codigo_interno || ''} ${p.descricao || ''} ${p.marca_nome || ''}`.toUpperCase();
        if (!txt.includes(q)) return false;
      }
      return true;
    });
  }, [produtos, busca, soRuins]);

  // `localizacao` saiu daqui: nao e mais campo de texto do produto, e a relacao (DA-324).
  async function salvarCampo(produtoId: number, campo: 'preco_pix', valor: any) {
    try {
      await api.patch(`/api/produtos/${produtoId}`, { [campo]: valor });
      setProdutos(prev => prev.map(p => p.id === produtoId ? { ...p, [campo]: valor } : p));
    } catch (err: any) {
      toast.erro('Erro ao salvar: ' + (err.message || 'falhou'));
    }
  }

  async function salvarLocal(produtoId: number, localizacaoId: number | null) {
    const antes = produtos.find(p => p.id === produtoId);
    // Otimista, com revert no erro: sem o revert a tela mostraria o lugar novo depois de um 4xx mudo
    // e o operador acharia que gravou.
    setProdutos(prev => prev.map(p => p.id === produtoId
      ? { ...p, localizacao_id: localizacaoId, localizacao_codigo: opcoesLocal.find(o => Number(o.value) === localizacaoId)?.label ?? null }
      : p));
    try {
      await api.patch(`/api/produtos/${produtoId}/localizacao-principal`, { localizacao_id: localizacaoId });
    } catch (err: any) {
      setProdutos(prev => prev.map(p => p.id === produtoId && antes ? antes : p));
      toast.erro('Erro ao salvar: ' + (err.message || 'falhou'));
    }
  }

  // Grid padrao so no destaque + setas: a linha edita Pix/Local ali mesmo, entao nao ha "acao
  // principal" pra Enter/duplo-clique disputar com os campos.
  const grid = useGridNavegavel<Produto>({ itens: filtrados, getId: (p) => p.id });
  const [fotoAmpliada, setFotoAmpliada] = useState<{ url: string; legenda: string } | null>(null);

  // Voltar da ficha com a linha marcada e na mesma rolagem (pedido dele, 04/11/2026) — a mesma
  // receita da lista de Produtos (`abrirProduto`): guarda ao sair, consome 1x ao voltar. Chave POR
  // NOTA, senao abrir outra nota herdaria a linha desta.
  const keyScroll = `revisar-margens.${id}.scroll`;
  const keySelecionado = `revisar-margens.${id}.selecionado`;
  function abrirProduto(produtoId: number) {
    const cont = document.querySelector('.app-content') as HTMLElement | null;
    if (cont) sessionStorage.setItem(keyScroll, String(cont.scrollTop));
    sessionStorage.setItem(keySelecionado, String(produtoId));
    navigate(`/produtos/${produtoId}`);
  }
  useEffect(() => {
    if (carregando) return;
    const raw = sessionStorage.getItem(keyScroll);
    if (raw == null) return;
    sessionStorage.removeItem(keyScroll);
    const y = Number(raw) || 0;
    const sel = sessionStorage.getItem(keySelecionado);
    if (sel != null) {
      sessionStorage.removeItem(keySelecionado);
      grid.setSelectedId(Number(sel) || null);
    }
    requestAnimationFrame(() => {
      const cont = document.querySelector('.app-content') as HTMLElement | null;
      if (cont) cont.scrollTop = y;
    });
  }, [carregando]);

  const totalRuins = useMemo(
    () => produtos.filter(p => abaixoDaMeta(p.preco_pix, p.preco_custo, metaDe(p))).length,
    [produtos],
  );

  return (
    <div>
      <div className="pb-3 sticky top-0 z-20" style={{ backgroundColor: 'var(--page-bg)' }}>
        <div className="pagina-header">
          <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => navigate('/notas-entrada')}
              className="btn-icon"
              title="Voltar"
              aria-label="Voltar"
              style={{ marginRight: 4 }}
            >
              <ArrowLeft size={20} />
            </button>
            Revisar margens
            <span className="text-sm muted">— Produtos importados na nota #{id}</span>
            {totalRuins > 0 && (
              <span className="ml-2 text-xs font-bold px-2 py-1 rounded" style={{ backgroundColor: '#fde68a', color: '#000' }}>
                {totalRuins} margem{totalRuins > 1 ? 's' : ''} abaixo da meta
              </span>
            )}
          </h2>
        </div>
      </div>

      <div className="mb-3 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
          <input
            type="text"
            value={busca}
            onChange={e => setBusca(upperMantendoCursor(e))}
            placeholder="BUSCAR POR CODIGO, DESCRICAO OU MARCA..."
            className="w-full pl-10 pr-3 py-2 border rounded-lg text-sm uppercase"
            style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
          />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={soRuins} onChange={e => setSoRuins(e.target.checked)} />
          Mostrar so margens abaixo da meta
        </label>
        <span className="text-xs muted ml-auto">{filtrados.length} de {produtos.length}</span>
      </div>

      {carregando && <div className="p-8 text-center muted">Carregando...</div>}

      {!carregando && produtos.length === 0 && (
        <div className="rounded-lg border px-4 py-12 text-center text-sm muted" style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--card-bg)' }}>
          Nenhum produto vinculado nesta nota.
        </div>
      )}

      {/* O `overflow-hidden` daqui (posto so pra arredondar o canto) CORTAVA calado o que nao coubesse:
          em tablet retrato (768px) a tabela pede 805px, e a coluna LOCAL e o lapis sumiam sem barra
          nenhuma pra alcanca-los. `overflow-x-auto` arredonda igual e deixa rolar ate o fim. */}
      {!carregando && produtos.length > 0 && (
        <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--card-bg)' }}>
          <table className="w-full text-sm border-collapse [&_td]:border [&_td]:border-gray-300 [&_th]:border [&_th]:border-gray-300">
            <thead>
              <tr style={{ backgroundColor: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}>
                <th className="text-center px-2 py-2 font-semibold w-14">FOTO</th>
                <th className="text-left px-2 py-2 font-semibold w-20">COD.</th>
                <th className="text-left px-3 py-2 font-semibold">DESCRICAO</th>
                <th className="text-left px-3 py-2 font-semibold" style={{ width: 120, minWidth: 120 }}>COD FORNECEDOR</th>
                {/* Marca, Custo, Pix e Mrg Pix +20% sobre o medido em 04/11/2026 (122/85/99/70px), pedido dele. */}
                <th className="text-left px-3 py-2 font-semibold" style={{ width: 146, minWidth: 146 }}>MARCA</th>
                <th className="text-center px-2 py-2 font-semibold w-12" title="Unidade do cadastro do produto">UN.</th>
                <th className="text-center px-2 py-2 font-semibold w-12" title="Unidade que veio no XML da NF">UN NF</th>
                <th className="text-right px-3 py-2 font-semibold" style={{ width: 102, minWidth: 102 }}>CUSTO (R$)</th>
                <th className="text-right px-3 py-2 font-semibold" style={{ width: 119, minWidth: 119 }}>PIX (R$)</th>
                <th className="text-center px-3 py-2 font-semibold" style={{ width: 84, minWidth: 84 }}>MRG PIX</th>
                <th className="text-right px-3 py-2 font-semibold w-20">ESTOQUE</th>
                <th className="text-left px-3 py-2 font-semibold w-24">LOCAL</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((p, idx) => {
                const mrg = calcMargem(p.preco_custo, p.preco_pix);
                const cor = corMargem(mrg, abaixoDaMeta(p.preco_pix, p.preco_custo, metaDe(p)));
                const cad = String(p.unidade_compra || p.unidade_sigla || '').trim().toUpperCase();
                const nf = String(p.nf_unidade || '').trim().toUpperCase();
                const divergente = cad && nf && cad !== nf;
                const rp = grid.rowProps(p);
                return (
                  <tr
                    key={p.id}
                    {...rp}
                    // Tab entrando num campo da linha (Pix, Local) tambem a marca (pedido dele, 04/11/2026).
                    onFocus={() => { if (grid.selectedId !== p.id) grid.setSelectedId(p.id); }}
                    // A zebra fica por baixo; selecionada, o style do hook sobrescreve o fundo.
                    style={{ backgroundColor: idx % 2 === 1 ? 'var(--grid-row-alt-bg)' : 'var(--grid-row-bg)', ...rp.style }}
                  >
                    <td className="px-1 py-1.5 text-center">
                      {p.foto_url ? (
                        // Clique amplia (pedido dele, 04/11/2026) — o mesmo LightboxImagem do Orcamento.
                        <FotoProduto
                          url={p.foto_url}
                          tam={36}
                          style={{ margin: '0 auto', cursor: 'zoom-in' }}
                          title="Clique para ampliar a foto"
                          onClick={() => setFotoAmpliada({ url: p.foto_url!, legenda: `${p.codigo_interno ?? ''} — ${p.descricao}` })}
                        />
                      ) : (
                        <div className="w-9 h-9 rounded flex items-center justify-center mx-auto" style={{ backgroundColor: 'var(--input-bg)' }}>
                          <ImageOff size={12} className="opacity-30" />
                        </div>
                      )}
                    </td>
                    {/* Texto das celulas em 14px preto (pedido dele, 04/11/2026). */}
                    <td className="px-2 py-1.5 codigo-curto text-[14px]" style={{ color: '#000' }}>{p.codigo_interno || '—'}</td>
                    <td className="px-3 py-1.5 text-[14px]" style={{ color: '#000' }}>{p.descricao}</td>
                    <td className="px-3 py-1.5 text-[14px]" style={{ color: '#000' }}>{codFornPorProduto.get(p.id) || '—'}</td>
                    <td className="px-3 py-1.5 text-[14px]" style={{ color: '#000' }}>{p.marca_nome || '—'}</td>
                    <td className="px-2 py-1.5 text-[14px] text-center" style={{ color: '#000' }}>{p.unidade_compra || p.unidade_sigla || '—'}</td>
                    <td
                      className="px-2 py-1.5 text-[14px] text-center font-bold"
                      style={divergente ? { backgroundColor: '#fde68a', color: '#000' } : { color: '#000' }}
                      title={divergente ? `Unidade NF (${nf}) ≠ cadastro (${cad}). Verifique se o estoque/custo entraram corretos.` : 'Unidade que veio no XML'}
                    >
                      {p.nf_unidade || '—'}
                    </td>
                    <td className="px-3 py-1.5 text-right text-[14px] font-medium" style={{ color: '#000' }}>{formatMoeda(p.preco_custo || 0)}</td>
                    <td className="px-2 py-1.5 text-right">
                      <PixInline valorAtual={p.preco_pix} onSalvar={(novo) => salvarCampo(p.id, 'preco_pix', novo)} />
                    </td>
                    <td className="px-3 py-1.5 text-center font-bold" style={{ backgroundColor: cor.bg, color: cor.bg !== 'transparent' ? '#000' : undefined }}>
                      {cor.label}
                    </td>
                    <td className="px-3 py-1.5 text-right text-[14px]" style={{ color: '#000' }}>{Number(p.estoque_atual || 0).toLocaleString('pt-BR')}</td>
                    <td className="px-2 py-1.5">
                      <LocalInline localId={p.localizacao_id} opcoes={opcoesLocal} onSalvar={(novo) => salvarLocal(p.id, novo)} />
                    </td>
                    <td className="px-1 py-1.5 text-center">
                      <button
                        onClick={() => abrirProduto(p.id)}
                        className="p-1 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Editar produto completo"
                      >
                        <Pencil size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {fotoAmpliada && (
        <LightboxImagem
          imagens={[{ url: fotoAmpliada.url, legenda: fotoAmpliada.legenda }]}
          onFechar={() => setFotoAmpliada(null)}
        />
      )}
    </div>
  );
}

function PixInline({ valorAtual, onSalvar }: { valorAtual: string; onSalvar: (n: number) => void }) {
  const [valor, setValor] = useState(fmtMoneyInput(valorAtual));
  const [editando, setEditando] = useState(false);
  useEffect(() => { setValor(fmtMoneyInput(valorAtual)); }, [valorAtual]);
  function commit() {
    const n = parseMoney(valor);
    if (n !== Number(valorAtual)) onSalvar(n);
    setValor(fmtMoneyInput(n));
    setEditando(false);
  }
  return (
    <input
      type="text"
      inputMode="decimal"
      value={valor}
      onChange={e => { setValor(e.target.value); setEditando(true); }}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') { setValor(fmtMoneyInput(valorAtual)); setEditando(false); (e.target as HTMLInputElement).blur(); }
      }}
      className={`w-24 px-1 py-1 border rounded text-[14px] text-right font-bold ${editando ? 'border-blue-400 bg-blue-50' : ''}`}
      style={{ backgroundColor: editando ? undefined : 'var(--input-bg)', borderColor: editando ? undefined : 'var(--input-border)' }}
    />
  );
}

// Seletor do CADASTRO de localizacoes (DA-324) — nao mais um input de texto livre. O texto livre era
// o que gerava os 275 valores pra 97 lugares ('G 15' vs 'G15' vs 'G  15'): digitar cria variante,
// escolher nao.
//
// Move so a PRINCIPAL (`PATCH /:id/localizacao-principal`). Produto com 2+ lugares nao perde os
// outros por passar por aqui — quem edita a lista inteira e o ProdutoForm.
function LocalInline({ localId, opcoes, onSalvar }: {
  localId: number | null;
  opcoes: OpcaoCombo[];
  onSalvar: (v: number | null) => void;
}) {
  return (
    // 110 e o suficiente pros codigos do cadastro (G01, EX-LIZ) e devolve 25px por linha —
    // no tablet cada pixel a menos e um a menos pra rolar.
    <div style={{ width: 110 }}>
      <ComboBox
        opcoes={opcoes}
        value={localId ?? ''}
        onChange={(v) => onSalvar(v === '' || v === null ? null : Number(v))}
        placeholder="—"
      />
    </div>
  );
}
